import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { SearchableBrandSelect } from './SearchableBrandSelect';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Switch } from './ui/switch';
import { 
  Brain, 
  LayoutDashboard, 
  Users, 
  Store, 
  FileText, 
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  Sparkles,
  PlusSquare,
  MessageCircle,
  Activity,
  GitBranch,
  Target,
  Trophy,
  Rocket,
  PiggyBank,
  Megaphone,
  Shield,
  Building2,
  UserCog,
  Settings2,
  HelpCircle,
  BookOpen,
  Lightbulb,
  Upload,
  BarChart3,
  Calculator,
  Award,
  Bell,
  BookOpenCheck,
  Clock,
  History,
  Radio
} from 'lucide-react';

// Map section IDs from backend to frontend view IDs
const sectionToViewMap = {
  'command_center': 'command-center',
  'executive_dashboard': 'executive',
  'customer_analytics': 'customers',
  'cohort_migration': 'cohort-migration',
  'rfm_churn': 'rfm-churn',
  'store_performance': 'store-performance',
  'points_economy': 'points-economics',
  'ai_conversation': 'ai-chat',
  'report_builder': 'report-builder',
  'campaign_builder': 'campaign-builder'
};

// Items grouped into sections for clarity. Super admin section moves to bottom.
const navItems = [
  // ── Dashboards ──
  { id: 'command-center', label: 'Command Center', icon: Activity, highlight: true, sectionId: 'command_center', group: 'dashboards' },
  { id: 'brand-insights', label: 'Brand AI Insights', icon: Brain, highlight: true, group: 'dashboards' },
  { id: 'cohort-migration', label: 'Cohort & Migration', icon: GitBranch, highlight: true, sectionId: 'cohort_migration', group: 'dashboards' },
  { id: 'rfm-churn', label: 'RFM & Churn', icon: Target, highlight: true, sectionId: 'rfm_churn', group: 'dashboards' },
  { id: 'store-performance', label: 'Store Leaderboard', icon: Trophy, highlight: true, sectionId: 'store_performance', group: 'dashboards' },
  { id: 'points-economics', label: 'Points Economics', icon: PiggyBank, highlight: true, sectionId: 'points_economy', group: 'dashboards' },
  { id: 'executive', label: 'Executive Dashboard', icon: LayoutDashboard, sectionId: 'executive_dashboard', group: 'dashboards' },
  { id: 'customers', label: 'Customer Analytics', icon: Users, sectionId: 'customer_analytics', group: 'dashboards' },
  { id: 'stores', label: 'Store Performance', icon: Store, sectionId: 'store_performance', group: 'dashboards' },

  // ── Campaigns ──
  { id: 'campaign-builder', label: 'Campaign Builder', icon: Megaphone, highlight: true, sectionId: 'campaign_builder', group: 'campaigns' },
  { id: 'campaign-analytics', label: 'Campaign Analytics', icon: BarChart3, sectionId: 'campaign_builder', group: 'campaigns' },
  { id: 'campaign-roi', label: 'Campaign ROI', icon: Rocket, sectionId: 'campaign_builder', group: 'campaigns' },
  { id: 'sms-builder', label: 'SMS Builder', icon: MessageCircle, sectionId: 'campaign_builder', group: 'campaigns' },

  // ── Tools ──
  { id: 'ai-chat', label: 'AI Conversation', icon: MessageSquare, sectionId: 'ai_conversation', group: 'tools' },
  { id: 'reports', label: 'AI Reports', icon: FileText, sectionId: 'report_builder', group: 'tools' },
  { id: 'data-reports', label: 'Data Reports', icon: FileText, sectionId: 'report_builder', group: 'tools' },
  { id: 'report-builder', label: 'Report Builder', icon: PlusSquare, sectionId: 'report_builder', group: 'tools' },
  { id: 'loyalty-config', label: 'Loyalty Rules', icon: Award, group: 'tools' },
  { id: 'customer-tools', label: 'Points & Coupons', icon: Sparkles, superAdminOnly: true, group: 'tools' },

  // ── Super Admin (bottom) ──
  { id: 'notification-center', label: 'Data Quality', icon: Bell, isNew: true, superAdminOnly: true, group: 'admin' },
  { id: 'metric-formulas', label: 'Metric Formulas', icon: BookOpenCheck, isNew: true, superAdminOnly: true, group: 'admin' },
  { id: 'data-sync', label: 'Data Sync', icon: Activity, superAdminOnly: true, group: 'admin' },
  { id: 'historic-sync', label: 'Historic Sync', icon: History, isNew: true, superAdminOnly: true, group: 'admin' },
  { id: 'daily-scheduler', label: 'Daily Scheduler', icon: Clock, isNew: true, superAdminOnly: true, group: 'admin' },
  { id: 'api-monitor', label: 'Live API Monitor', icon: Radio, isNew: true, adminOnly: true, group: 'admin' },
  { id: 'csv-upload', label: 'CSV Data Upload', icon: Upload, superAdminOnly: true, group: 'admin' },
  { id: 'brand-management', label: 'Brand Management', icon: Building2, superAdminOnly: true, group: 'admin' },
  { id: 'user-management', label: 'User Management', icon: UserCog, adminOnly: true, group: 'admin' },
  { id: 'platform-settings', label: 'Platform Settings', icon: Settings2, superAdminOnly: true, group: 'admin' },
];

const GROUP_LABELS = {
  dashboards: 'Dashboards',
  campaigns: 'Campaigns',
  tools: 'Tools',
  admin: 'Super Admin',
};

export default function DashboardSidebar({ collapsed, onToggle, activeView, onViewChange }) {
  const { user, logout, isSuperAdmin, allowedSections, brand } = useAuth();
  const { brands, selectedBrand, selectBrand, setSelectedBrand } = useChat();
  const navigate = useNavigate();

  // Filter nav items based on user's allowed sections and role
  const filteredNavItems = navItems.filter(item => {
    // Super admin only items
    if (item.superAdminOnly) {
      return isSuperAdmin;
    }
    // Admin-only items visible to super_admin and brand_admin
    if (item.adminOnly) {
      return isSuperAdmin || user?.role === 'brand_admin';
    }
    // Super admin sees everything
    if (isSuperAdmin) return true;
    // If no section restriction for this item, show it
    if (!item.sectionId) return true;
    // Check if user has permission
    return allowedSections.includes(item.sectionId);
  });

  const handleBrandChange = (brandId) => {
    // brandId from Select is a string, brand_id in brands might be number or string
    const foundBrand = brands.find(b => String(b.brand_id) === String(brandId));
    if (foundBrand) {
      selectBrand(foundBrand);
    }
  };

  const handleLogout = async () => {
    await logout();
    // logout() already handles the redirect to /login
  };

  // Platform is always "Fundle Brain", brand name shows in selector
  const platformName = 'Fundle Brain';
  const brandLogo = selectedBrand?.logo_url || brand?.logo_url;

  return (
    <aside 
      className={cn(
        "h-full bg-card border-r border-border flex flex-col transition-all duration-300",
        collapsed ? "w-20" : "w-64"
      )}
      data-testid="dashboard-sidebar"
    >
      {/* Logo - Always shows Fundle Brain - Clickable to go home for super admin */}
      <div className="p-4 border-b border-border">
        <div 
          className={cn(
            "flex items-center gap-3",
            isSuperAdmin && "cursor-pointer hover:opacity-80 transition-opacity"
          )}
          onClick={() => {
            if (isSuperAdmin) {
              setSelectedBrand(null);
              navigate('/');
            }
          }}
          title={isSuperAdmin ? "Go to Home" : "Fundle Brain"}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center flex-shrink-0">
            <Brain className="w-6 h-6 text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                {platformName}
              </h1>
              <p className="text-xs text-muted-foreground">AI Analytics</p>
            </div>
          )}
          {!collapsed && isSuperAdmin && (
            <span className="ml-auto flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-yellow-500/20 text-yellow-400 font-medium">
              <Shield className="w-3 h-3" />
              Admin
            </span>
          )}
        </div>
      </div>

      {/* Brand Selector - Enhanced with Searchable Dropdown and Home option */}
      {!collapsed && (isSuperAdmin ? brands.length > 0 : brands.length > 1) && (
        <div className="p-4 border-b border-border">
          <label className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
            <Building2 className="w-3 h-3" />
            {isSuperAdmin ? 'Switch Brand' : 'Current Brand'}
          </label>
          {isSuperAdmin ? (
            <SearchableBrandSelect
              brands={brands}
              selectedBrand={selectedBrand}
              onSelect={(brand) => {
                if (!brand) {
                  // Home selected - go back to landing page
                  setSelectedBrand(null);
                  navigate('/');
                } else {
                  handleBrandChange(brand.brand_id?.toString());
                }
              }}
              placeholder="Select brand"
              showIndustry={true}
              showHomeOption={true}
            />
          ) : (
            <div className="px-3 py-2 bg-muted rounded-lg text-sm font-medium">
              {selectedBrand?.name || 'Loading...'}
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto" data-testid="sidebar-nav">
        {Object.entries(GROUP_LABELS).map(([groupKey, groupLabel]) => {
          const groupItems = filteredNavItems.filter(it => (it.group || 'dashboards') === groupKey);
          if (groupItems.length === 0) return null;
          return (
            <div key={groupKey} className="mb-3">
              {!collapsed && (
                <div className="px-3 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70" data-testid={`sidebar-group-${groupKey}`}>
                  {groupLabel}
                </div>
              )}
              {collapsed && groupKey !== 'dashboards' && (
                <div className="my-2 mx-2 border-t border-border/50" />
              )}
              {groupItems.map((item) => {
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => onViewChange(item.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200",
                      isActive 
                        ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25" 
                        : item.highlight && !isActive
                          ? "bg-gradient-to-r from-violet-500/10 to-pink-500/10 border border-violet-500/20 hover:border-violet-500/40 text-foreground"
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    )}
                    data-testid={`nav-${item.id}`}
                    data-tour={item.id}
                  >
                    <item.icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-primary-foreground")} />
                    {!collapsed && (
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    )}
                    {!collapsed && item.id === 'ai-chat' && (
                      <Sparkles className="w-4 h-4 ml-auto text-yellow-500" />
                    )}
                    {!collapsed && item.isNew && (
                      <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-400 font-medium">NEW</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="p-3 border-t border-border space-y-1">
        {/* Help Center */}
        <button
          onClick={() => navigate('/help')}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
          data-testid="help-button"
          data-tour="help-button"
        >
          <HelpCircle className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span className="text-sm font-medium">Help Center</span>}
        </button>
        
        {/* Settings with Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Settings</span>}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56">
            <DropdownMenuItem 
              className="flex items-center justify-between cursor-pointer"
              onSelect={(e) => {
                e.preventDefault();
                const current = localStorage.getItem('toursDisabled') === 'true';
                localStorage.setItem('toursDisabled', (!current).toString());
                window.location.reload();
              }}
            >
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                <span>Guided Tours</span>
              </div>
              <Switch 
                checked={localStorage.getItem('toursDisabled') !== 'true'}
                onCheckedChange={() => {}}
              />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="cursor-pointer"
              onSelect={() => {
                localStorage.removeItem('completedTours');
                localStorage.setItem('toursDisabled', 'false');
                window.location.reload();
              }}
            >
              Reset All Tours
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User info & Logout */}
        {user && (
          <div className={cn(
            "flex items-center gap-3 p-3 rounded-xl bg-muted/50",
            collapsed && "justify-center"
          )}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.role ? user.role.replace('_', ' ') : user.email}
                </p>
              </div>
            )}
            {!collapsed && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout}
                className="flex-shrink-0"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}

        {/* Collapse toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="w-full justify-center"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>

        {/* Powered by Fundle footer */}
        {!collapsed && (
          <div className="pt-2 text-center">
            <p className="text-[10px] text-muted-foreground/60">
              Powered by{' '}
              <a href="https://fundle.ai" target="_blank" rel="noopener noreferrer" className="text-primary/60 hover:text-primary hover:underline">
                Fundle
              </a>
            </p>
          </div>
        )}
      </div>
    </aside>
  );
}
